/** The one source of randomness in map generation.
 *
 * Every roll advances the `map.generation` stream seeded from the map seed, so
 * a seed is the whole description of a map: two `generateMap` calls with the
 * same seed produce byte-identical arrays. `Math.random` never appears here,
 * and neither does anything whose result is engine-dependent - `Math.cos` and
 * friends are not bit-reproducible across JS engines, which is why the few
 * places that want a direction read an integer table instead.
 */

import { type RngStreamState, nextBounded, seedStream } from "@game/core";

export interface Rng {
  state: RngStreamState;
}

export const makeRng = (seed: number): Rng => ({
  state: seedStream(seed | 0, "map.generation"),
});

/**
 * The generator's SECOND stream, for what an object carries rather than where
 * it stands. Every draw from `GenState.r` moves every placement after it, so a
 * new roll on the main stream re-deals the whole map for every seed a test
 * had picked by hand - it happened with the garrison guard on 2026-09-02.
 * Payload rolls go here instead and leave the layout alone. New randomness
 * that is not a placement belongs on this stream.
 */
export const makeSideRng = (seed: number): Rng => ({
  state: seedStream(seed | 0, "map.generation.side"),
});

/** Uniform in [0, bound). */
export function int(r: Rng, bound: number): number {
  if (bound <= 1) return 0;
  const [s, v] = nextBounded(r.state, bound);
  r.state = s;
  return v;
}

/** Uniform in [lo, hi], inclusive. */
export const range = (r: Rng, lo: number, hi: number): number =>
  hi <= lo ? lo : lo + int(r, hi - lo + 1);

/** True with probability `bp` basis points (D4). */
export const chance = (r: Rng, bp: number): boolean => int(r, 10000) < bp;

export function pick<T>(r: Rng, xs: readonly T[]): T | null {
  if (xs.length === 0) return null;
  return xs[int(r, xs.length)] ?? null;
}

/** Fisher-Yates, in place, so the caller controls the allocation. */
export function shuffle<T>(r: Rng, xs: T[]): T[] {
  for (let i = xs.length - 1; i > 0; i--) {
    const j = int(r, i + 1);
    const a = xs[i]!, b = xs[j]!;
    xs[i] = b;
    xs[j] = a;
  }
  return xs;
}

/**
 * Weighted pick. Weights are integers; a zero weight is never chosen. Returns
 * `null` only when every weight is zero.
 */
export function weighted<T>(r: Rng, xs: readonly T[], weight: (x: T) => number): T | null {
  let total = 0;
  for (const x of xs) {
    const w = weight(x);
    if (w > 0) total += w;
  }
  if (total <= 0) return null;
  let roll = int(r, total);
  for (const x of xs) {
    const w = weight(x);
    if (w <= 0) continue;
    roll -= w;
    if (roll < 0) return x;
  }
  return xs[xs.length - 1] ?? null;
}

/**
 * Sixteen compass directions as integer unit vectors scaled by 1000. Player
 * start zones are laid out on this ring; a trig call here would make the map
 * depend on the host's libm.
 */
export const DIR16: readonly (readonly [number, number])[] = Object.freeze([
  [1000, 0], [924, 383], [707, 707], [383, 924],
  [0, 1000], [-383, 924], [-707, 707], [-924, 383],
  [-1000, 0], [-924, -383], [-707, -707], [-383, -924],
  [0, -1000], [383, -924], [707, -707], [924, -383],
]);
